/**
 * #3991: when focus or visibility changes, what should happen to a PENDING offline transition?
 *
 * Extracted as a pure function because this decision has been wrong twice, in opposite directions:
 *
 *  1. It originally gated on window FOCUS. But a window can be blurred while still fully VISIBLE
 *     (the app in a side-by-side window, or DevTools focused), and in that state the offline
 *     indicator was suppressed entirely — the app went on claiming to be online while it plainly
 *     was not.
 *
 *  2. Gating on visibility alone then let the opposite slip through: a running debounce timer was
 *     not cancelled when the page became hidden, so Offline could still commit for a backgrounded
 *     app — which is the very case the suppression exists for (a backgrounded app has its
 *     networking suspended by the OS, so it must not claim to be offline when it is merely asleep).
 *
 * The rule that satisfies both: **cancel when hidden, schedule when visible — and never restart a
 * timer that is already running**, because a user alt-tabbing back and forth would otherwise keep
 * resetting the 5-second countdown and the indicator would never land at all.
 *
 * `shouldExtendOfflineDebounceOnReconnect` further down is the one sanctioned exception to that
 * last part, and its own comment explains what keeps it bounded.
 */
export enum OfflineDebounceAction {
  /** Page is hidden: cancel any pending offline transition. */
  Cancel = 'cancel',
  /** Page is visible and an offline transition is due but not yet on its way: start the debounce. */
  Schedule = 'schedule',
  /** Nothing to do — already scheduled, already committed, or not offline at all. */
  LeaveAlone = 'leave-alone',
}

export function decideOfflineDebounceAction(input: {
  /** Is the page actually on screen? NOT "is the window focused" — see the note above. */
  pageVisible: boolean;
  /** Has the database layer reported Offline? */
  rawStateIsOffline: boolean;
  /** Have we already committed Offline to the UI? */
  debouncedStateIsOffline: boolean;
  /** Is a debounce countdown already running? */
  timerAlreadyRunning: boolean;
}): OfflineDebounceAction {
  // hidden — a backgrounded app must not claim to be offline, so drop anything pending
  if (!input.pageVisible) return OfflineDebounceAction.Cancel;

  // visible, offline, not yet shown, and nothing already counting down → start the countdown
  if (input.rawStateIsOffline && !input.debouncedStateIsOffline && !input.timerAlreadyRunning) {
    return OfflineDebounceAction.Schedule;
  }

  // deliberately NOT restarting a running timer: alt-tabbing would reset it forever
  return OfflineDebounceAction.LeaveAlone;
}

/**
 * df-c29bf5fc50cd: should a running offline countdown be given one more 5 seconds because we have
 * just learned the network is back?
 *
 * The countdown starts when the page becomes visible while the database layer still says Offline.
 * What it is waiting for is three network steps in a row — the reachability ping, then the Firebase
 * session wait plus the CouchDB login, then restarting sync and its own "active" event — and only
 * that last event stops the countdown. So the countdown races the recovery it exists to wait for.
 * Measured over 2 days of real resumes, the first two steps alone took longer than the whole 5
 * seconds on about 1 resume in 120, and the user was then shown "Offline" while both our server and
 * the database were answering normally.
 *
 * Restarting rather than cancelling is deliberate. Cancelling would mean a sync that never comes
 * back never says so at all, and that case is real: in 14 days of the `connectivity_split_brain`
 * measurement, 9.5% of the commits found the database genuinely unreachable on a working network.
 * The pill's job is to say "sync has given up", so it must still be able to say it — just five
 * seconds later, counted from the moment we knew the network was fine.
 *
 * What stops this becoming the never-landing countdown the note at the top of this file warns
 * about. The connection-state event only fires on a real CHANGE of connection state, so a steady
 * connection cannot re-trigger it. And only ONE extension is granted per pending transition: the
 * caller clears `extensionAlreadySpent` at the same moment it clears the countdown, which is when
 * the pending transition ends. The worst case is 10 seconds instead of 5, never unbounded.
 */
export function shouldExtendOfflineDebounceOnReconnect(input: {
  /** Has the connection state just become Online? (A flip to Offline is somebody else's business —
   *  the database layer reports that itself, which reschedules the countdown by the normal route.) */
  connectionNowOnline: boolean;
  /** Is there a countdown to extend? With nothing pending there is nothing to protect. */
  countdownRunning: boolean;
  /** Has this pending transition already had its one extension? */
  extensionAlreadySpent: boolean;
}): boolean {
  return input.connectionNowOnline && input.countdownRunning && !input.extensionAlreadySpent;
}
